import { Button } from "@app/ui";
import type { I18nKey } from "../../lib/language.js";
import { useI18n } from "../../lib/useI18n.js";
import { sidebarPartVisible } from "../../lib/sidebar-visibility.js";
import { useMobileUx } from "../../lib/useMobileUx.js";
import { useSettingsStore } from "../../stores/index.js";
import type { SidebarPart } from "../../stores/settings.store.js";
import { SettingsCard } from "./SettingsCard.js";
import { SettingsSection } from "./SettingsSection.js";
import { ShortcutsCard } from "./ShortcutsSettings.js";

const PARTS: { key: SidebarPart; labelKey: I18nKey; descKey: I18nKey }[] = [
  { key: "today", labelKey: "navigation.part.today", descKey: "navigation.part.today.desc" },
  { key: "calendar", labelKey: "navigation.part.calendar", descKey: "navigation.part.calendar.desc" },
  { key: "tasks", labelKey: "navigation.part.tasks", descKey: "navigation.part.tasks.desc" },
  { key: "priorities", labelKey: "navigation.part.priorities", descKey: "navigation.part.priorities.desc" },
  { key: "inbox", labelKey: "navigation.part.inbox", descKey: "navigation.part.inbox.desc" },
  { key: "search", labelKey: "navigation.part.search", descKey: "navigation.part.search.desc" },
  { key: "claude", labelKey: "navigation.part.claude", descKey: "navigation.part.claude.desc" },
  { key: "filters", labelKey: "navigation.part.filters", descKey: "navigation.part.filters.desc" },
];

/** "Navigation" — sidebar visibility plus (desktop only) keyboard shortcuts. */
export function NavigationSettings() {
  const { t } = useI18n();
  const mobile = useMobileUx();
  const sidebarVisible = useSettingsStore((s) => s.sidebarVisible);
  const matrixScope = useSettingsStore((s) => s.matrixScope);
  const prioritiesOverride = useSettingsStore((s) => s.prioritiesNavVisibleOverride);
  const sidebarNavOrder = useSettingsStore((s) => s.sidebarNavOrder);
  const setSidebarVisible = useSettingsStore((s) => s.setSidebarVisible);
  const resetPrioritiesVisibility = useSettingsStore((s) => s.resetPrioritiesNavVisibility);
  const orderedParts = [
    ...sidebarNavOrder
      .map((key) => PARTS.find((p) => p.key === key))
      .filter((p): p is (typeof PARTS)[number] => !!p),
    ...PARTS.filter((p) => p.key === "filters" || !sidebarNavOrder.includes(p.key)),
  ];

  return (
    <div className="flex flex-col gap-8">
      <SettingsSection title={t("navigation.sidebar")}>
        <SettingsCard
          id="sidebar-visibility"
          title={t("navigation.showInSidebar")}
          description={t("navigation.showInSidebar.description")}
        >
          <ul className="flex flex-col divide-y divide-border">
            {orderedParts.map(({ key, labelKey, descKey }) => {
              const shown = sidebarPartVisible(key, {
                visible: sidebarVisible,
                matrixScope,
                prioritiesOverride,
              });
              return <li key={key} className="flex flex-col gap-2 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <div>
                  <div className="text-sm font-medium">{t(labelKey)}</div>
                  <div className="text-xs text-muted-foreground">{t(descKey)}</div>
                </div>
                <div className="flex shrink-0 items-center gap-1 self-end">
                  {key === "priorities" && prioritiesOverride !== null && (
                    <Button variant="ghost" size="sm" onClick={() => void resetPrioritiesVisibility()}>
                      {t("navigation.useAutomatic")}
                    </Button>
                  )}
                  <Button
                    variant={shown ? "primary" : "outline"}
                    size="sm"
                    onClick={() => void setSidebarVisible(key, !shown)}
                  >
                    {shown ? t("common.shown") : t("common.hidden")}
                  </Button>
                </div>
              </li>;
            })}
          </ul>
        </SettingsCard>
      </SettingsSection>

      {!mobile && (
        <SettingsSection title={t("navigation.keyboardShortcuts")}>
          <ShortcutsCard />
        </SettingsSection>
      )}
    </div>
  );
}
